import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import { GetAccountResponse } from '../../types/accounts/accounts.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Get account
export const getAccount = async (): Promise<GetAccountResponse> => {
  try {
    const response = await api.get('/accounts/');
    return response.data;
  } catch (error) {
    console.error('Error fetching account:', error);
    throw error;
  }
};

// Invite user to account
export const inviteUserToAccount = async (data: {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'member';
  permissions: {
    type: 'all' | 'custom';
    workspaces?: { [workspaceId: string]: string };
  };
}): Promise<{ message: string }> => {
  try {
    const response = await api.post('/accounts/invite', data);
    return response.data;
  } catch (error) {
    console.error('Error inviting user to account:', error);
    throw error;
  }
};

// Delete user from account
export const deleteUser = async (
  userId: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete(`/accounts/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Update user in account
export const updateUser = async (
  userId: string,
  data: {
    firstName: string;
    lastName: string;
    email: string;
    role: 'admin' | 'member';
    permissions: {
      type: 'all' | 'custom';
      workspaceRole?: 'admin' | 'member';
      workspaces?: { [workspaceId: string]: string };
    };
  }
): Promise<{ message: string }> => {
  try {
    const response = await api.put(`/accounts/${userId}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export default api;
